export type CropKeyframeWindow = { time: number; endTime: number };

/**
 * The crop keyframe the editor preview and the export apply at a source time:
 * the one whose `[time, endTime]` window covers it, else the latest keyframe
 * that started before it (held across a gap), else the first one.
 *
 * The backend mirrors this rule in `resolveCropCoverage.js`
 * (autoclipper-backend) when it builds the render's crop scenes — keep the two
 * in sync. They used to disagree: the preview fell back to the last keyframe
 * of the video while the export went dead-center, so a clip outside every
 * keyframe previewed the user's reframe and rendered without it.
 */
export function resolveCropKeyframeAtTime<K extends CropKeyframeWindow>(
  keyframes: readonly K[] | null | undefined,
  time: number,
): K | null {
  if (!keyframes || keyframes.length === 0) return null;
  let previous: K | null = null;
  let first: K | null = null;
  for (const keyframe of keyframes) {
    if (time >= keyframe.time && time <= keyframe.endTime) return keyframe;
    if (keyframe.time <= time && (!previous || keyframe.time >= previous.time)) previous = keyframe;
    if (!first || keyframe.time < first.time) first = keyframe;
  }
  return previous ?? first;
}
